import { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import {
  ArrowClockwiseIcon,
  ArrowSquareOutIcon,
  CaretDownIcon,
  CircleDashedIcon,
  GitCommitIcon,
  GitDiffIcon,
  GitMergeIcon,
  GitPullRequestIcon,
  NoteIcon,
  ProhibitIcon,
} from '@phosphor-icons/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import { useAgentSession } from '@/api-models/agent-sessions';
import {
  closePullRequest,
  generatePullRequest,
  type MergeMethod,
  mergePullRequest,
  revalidateGitViews,
  type PullRequest,
  type PullRequestState,
  startReview,
  useGitBranchDiff,
  useGitStatus,
  useSessionPullRequest,
} from '@/api-models/sandbox-git';
import { Button } from '@/ui/button/Button';
import { Input } from '@/ui/Input';
import { Spinner } from '@/ui/Spinner';
import { menuItemClasses } from '@/ui/menu-classes';
import { TabBar } from '@/ui/Tabs';
import classNames from '@/utils/classnames';
import { ApiError } from '@/utils/fetch.client';

import { BranchPair } from './review/BranchPair';
import { ChangesPanel } from './review/ChangesPanel';
import { CommitsList } from './review/CommitsList';
import { copyToClipboard } from './review/copy-to-clipboard';
import { DiffStat } from './review/DiffStat';
import { MENU_CONTENT } from './review/menu-classes';
import { pullRequestBlocker } from './review/pull-request-error';
import { PullRequestLink } from './review/PullRequestLink';
import { PullRequestPanel } from './review/PullRequestPanel';
import { ReviewGate } from './review/ReviewGate';
import {
  type DiffPrefs,
  readDiffPrefs,
  readReviewStarted,
  readViewed,
  writeDiffPrefs,
  writeReviewStarted,
  writeViewed,
} from './review/review-storage';

/** Used only until the session row (which carries the real base) has loaded. */
const FALLBACK_BASE_REF = 'main';

type ReviewSubTab = 'changes' | 'commits' | 'description';

const SUB_TABS = [
  { value: 'changes' as const, label: 'Changes', icon: <GitDiffIcon size={14} /> },
  { value: 'commits' as const, label: 'Commits', icon: <GitCommitIcon size={14} /> },
  { value: 'description' as const, label: 'Description', icon: <NoteIcon size={14} /> },
];

/**
 * Header actions for an existing PR: a plain "View PR" that opens the provider, plus a merge menu
 * (squash or merge commit) and a close action. Merge/close are hidden once the PR is terminal
 * (already merged or closed), since there is nothing left to do to it.
 */
function PullRequestActions({
  pull,
  onMerge,
  onClose,
  busy,
}: {
  pull: PullRequest;
  onMerge: (method: MergeMethod) => void;
  onClose: () => void;
  busy: boolean;
}) {
  const terminal = pull.state === 'merged' || pull.state === 'closed';
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size={8}
        iconLeft={<ArrowSquareOutIcon size={14} />}
        onClick={() => window.open(pull.html_url, '_blank', 'noopener,noreferrer')}
        title="Open the pull request on the provider"
      >
        View PR
      </Button>
      {!terminal && (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button type="button" size={8} isLoading={busy} disabled={busy} iconLeft={<GitMergeIcon size={14} />}>
              Merge
              <CaretDownIcon size={11} className="opacity-70" />
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content align="end" sideOffset={6} className={MENU_CONTENT}>
              <DropdownMenu.Item className={menuItemClasses} onSelect={() => onMerge('squash')}>
                <GitMergeIcon size={15} />
                <span className="flex-1">Squash and merge</span>
              </DropdownMenu.Item>
              <DropdownMenu.Item className={menuItemClasses} onSelect={() => onMerge('merge')}>
                <GitMergeIcon size={15} />
                <span className="flex-1">Create a merge commit</span>
              </DropdownMenu.Item>
              <div className="my-1 h-px bg-line-subtle" />
              <DropdownMenu.Item
                className={classNames(menuItemClasses, 'text-danger data-highlighted:text-danger')}
                onSelect={onClose}
              >
                <ProhibitIcon size={15} />
                <span className="flex-1">Close pull request</span>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      )}
    </>
  );
}

const STATE_BADGE: Record<PullRequestState, { label: string; icon: typeof GitPullRequestIcon; className: string }> = {
  open: { label: 'Open', icon: GitPullRequestIcon, className: 'bg-diff-add-bg text-diff-add' },
  draft: { label: 'Draft', icon: CircleDashedIcon, className: 'bg-surface-3 text-fg-secondary' },
  merged: { label: 'Merged', icon: GitMergeIcon, className: 'bg-success-tint text-success' },
  closed: { label: 'Closed', icon: ProhibitIcon, className: 'bg-diff-del-bg text-diff-del' },
};

/**
 * Accessible PR state chip: colored, but the icon and label carry the meaning so it never relies
 * on color alone. `null` renders the "not opened yet" state for a branch without a PR.
 */
function PrStateBadge({ state }: { state: PullRequestState | null }) {
  if (state === null) {
    return (
      <span className="flex shrink-0 items-center gap-1 rounded-full border border-line-subtle px-2 py-0.5 text-2xs text-fg-tertiary">
        <CircleDashedIcon size={12} />
        Not opened
      </span>
    );
  }
  const { label, icon: Icon, className } = STATE_BADGE[state];
  return (
    <span
      className={classNames(
        'flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-medium',
        className,
      )}
    >
      <Icon size={12} weight="fill" />
      {label}
    </span>
  );
}

export function ReviewTab({ base, sessionId }: { base: string; sessionId: string }) {
  const [subTab, setSubTab] = useState<ReviewSubTab>('changes');
  const { session } = useAgentSession(sessionId);
  const baseRef = session?.base_branch?.trim() || FALLBACK_BASE_REF;
  const { files, isLoading, error: filesError } = useGitBranchDiff(base, baseRef);
  const { status } = useGitStatus(base);
  const { pull, unavailable: pullUnknown, isLoading: pullLoading, mutate: mutatePull } = useSessionPullRequest(base);
  const [viewed, setViewed] = useState<string[]>(() => readViewed(base));
  const [diffPrefs, setDiffPrefs] = useState<DiffPrefs>(() => readDiffPrefs(base));
  const [started, setStarted] = useState<boolean>(() => readReviewStarted(base));

  // `null` means untouched, so the title/body keep tracking the PR (or the default) until the user
  // types — the session name the default is built from arrives a request later than first render.
  const [title, setTitle] = useState<string | null>(null);
  const [body, setBody] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await revalidateGitViews(base);
    } finally {
      setRefreshing(false);
    }
  }, [base]);

  // Write the created/updated PR straight into the hook's cache: revalidating instead would leave
  // the tab showing the old state for another round-trip, which reads as "nothing happened".
  const onSaved = useCallback(
    (saved: PullRequest) => {
      void mutatePull((prev) => ({ head: prev?.head ?? saved.head_ref, pull: saved, unavailable: null }), {
        revalidate: false,
      });
      // Let the header/body re-track the freshly saved PR.
      setTitle(null);
      setBody(null);
    },
    [mutatePull],
  );

  const [merging, setMerging] = useState(false);

  // Merge/close both land the PR then revalidate the git views so the state badge, commits and
  // status reflect the outcome. Guarded by a confirm: both are consequential and hard to undo.
  const mergePr = useCallback(
    async (method: MergeMethod) => {
      const prompt =
        method === 'squash' ? 'Squash and merge this pull request?' : 'Create a merge commit for this pull request?';
      if (!window.confirm(prompt)) return;
      setMerging(true);
      try {
        const saved = await mergePullRequest(base, { method });
        onSaved(saved);
        await revalidateGitViews(base);
        toast.success('Pull request merged');
      } catch (e) {
        toast.error(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Could not merge');
      } finally {
        setMerging(false);
      }
    },
    [base, onSaved],
  );

  const closePr = useCallback(async () => {
    if (!window.confirm('Close this pull request without merging?')) return;
    setMerging(true);
    try {
      const saved = await closePullRequest(base);
      onSaved(saved);
      await revalidateGitViews(base);
      toast.success('Pull request closed');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Could not close');
    } finally {
      setMerging(false);
    }
  }, [base, onSaved]);

  const changeViewed = useCallback(
    (next: string[]) => {
      setViewed(next);
      writeViewed(base, next);
    },
    [base],
  );

  const changeDiffPrefs = useCallback(
    (next: DiffPrefs) => {
      setDiffPrefs(next);
      writeDiffPrefs(base, next);
    },
    [base],
  );

  const branch = status?.current_branch ?? session?.branch ?? 'current branch';
  const sessionName = session?.name?.trim();
  const defaultTitle = sessionName ? `${sessionName} (${branch})` : branch;
  const titleValue = title ?? pull?.title ?? defaultTitle;
  // Same untouched-tracking as the title: show the PR's own description until the user edits it.
  const bodyValue = body ?? pull?.body ?? '';

  // Held back until a branch is actually known: `pullRequestBlocker` reads an absent one as
  // "this session has no branch", which would flash on every mount.
  const blocker = status || session ? pullRequestBlocker(status?.current_branch ?? session?.branch, baseRef) : null;

  const generate = useCallback(async () => {
    setGenerating(true);
    setGenerateError(null);
    try {
      const draft = await generatePullRequest(base);
      setTitle(draft.title);
      setBody(draft.body);
      toast.success('Draft generated');
    } catch (e) {
      const message = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Generation failed';
      setGenerateError(message);
    } finally {
      setGenerating(false);
    }
  }, [base]);

  // One action to open a review: commit the agent's outstanding work, draft the title + body, then
  // refresh the git views so the new commit and any status change land immediately. Drops the user
  // on the Description tab where the freshly drafted copy is waiting.
  const beginReview = useCallback(async () => {
    setStarting(true);
    setGenerateError(null);
    try {
      const result = await startReview(base);
      setTitle(result.title);
      setBody(result.body);
      setStarted(true);
      writeReviewStarted(base);
      setSubTab('description');
      await revalidateGitViews(base);
      toast.success(result.committed ? 'Committed latest work and drafted the review' : 'Drafted the review');
    } catch (e) {
      const message = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Could not start review';
      setGenerateError(message);
      toast.error(message);
    } finally {
      setStarting(false);
    }
  }, [base]);

  // Nothing renders until a review is started. A branch that already has a PR counts as started —
  // its work is committed and pushed, so re-gating it behind another checkpoint commit is wrong.
  const revealed = started || pull !== null;
  if (!revealed) {
    if (pullLoading) {
      return (
        <div className="flex min-h-0 flex-1 items-center justify-center bg-surface-1">
          <Spinner size={5} />
        </div>
      );
    }
    return (
      <ReviewGate
        base={baseRef}
        branch={branch}
        onStart={beginReview}
        starting={starting}
        error={generateError}
        blocker={blocker}
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-surface-1">
      {/* Persistent header: editable title + state, above the sub-tabs so it stays put on every tab. */}
      <div className="flex shrink-0 flex-col gap-2 border-b border-line-subtle px-3 py-2.5">
        <div className="flex items-center gap-2">
          <Input
            inputSize={8}
            value={titleValue}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Pull request title"
            aria-label="Pull request title"
            className="flex-1"
          />
          <PrStateBadge state={pull?.state ?? null} />
          {pull && <PullRequestActions pull={pull} onMerge={mergePr} onClose={closePr} busy={merging} />}
          <Button
            type="button"
            variant="ghost"
            shape="square"
            size={8}
            onClick={refresh}
            disabled={refreshing || starting}
            aria-label="Refresh review data"
            title="Refresh commits, status and PR"
          >
            <ArrowClockwiseIcon size={15} className={refreshing ? 'animate-spin' : undefined} />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <BranchPair base={baseRef} head={branch} onCopyHead={() => copyToClipboard(branch, 'Branch name copied')} />
          {status?.branch_published === false && (
            <span className="shrink-0 rounded bg-diff-add-bg px-1.5 py-0.5 text-2xs text-diff-add">new branch</span>
          )}
          {status && (status.ahead > 0 || status.behind > 0) && (
            <span className="shrink-0 text-2xs tabular-nums text-fg-tertiary">
              ↑{status.ahead} ↓{status.behind}
            </span>
          )}
          {pull && (
            <span className="ml-auto flex shrink-0 items-center gap-3 text-2xs tabular-nums text-fg-tertiary">
              {(pull.additions !== null || pull.deletions !== null) && (
                <DiffStat additions={pull.additions ?? 0} deletions={pull.deletions ?? 0} />
              )}
              <PullRequestLink pull={pull} />
            </span>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center border-b border-line-subtle px-2 py-1">
        <TabBar value={subTab} onChange={setSubTab} tabs={SUB_TABS} ariaLabel="Review" />
      </div>

      {subTab === 'changes' && (
        <ChangesPanel
          base={base}
          files={files}
          isLoading={isLoading}
          error={filesError}
          viewed={viewed}
          onViewedChange={changeViewed}
          diffPrefs={diffPrefs}
          onDiffPrefsChange={changeDiffPrefs}
        />
      )}

      {subTab === 'commits' && (
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <CommitsList base={base} baseRef={baseRef} />
        </div>
      )}

      {subTab === 'description' && (
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <PullRequestPanel
            base={base}
            title={titleValue}
            body={bodyValue}
            onBodyChange={setBody}
            blocker={blocker}
            pull={pull}
            loading={pullLoading}
            unknown={pullUnknown}
            onSaved={onSaved}
            onGenerate={generate}
            generating={generating}
            generateError={generateError}
          />
        </div>
      )}
    </div>
  );
}
